import { useCallback, useEffect, useMemo } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';

import UILoader from '@aquariuxfintech/aqx-trader-react-ui/loader';
import { closeAllPortals } from '@aquariuxfintech/aqx-trader-react-ui/portal';
import { EUISnackBarMessageType, showBasicSnackBar } from '@aquariuxfintech/aqx-trader-react-ui/snack-bar';

import { ELoginSource } from '@aqx-core/common/api/auth';
import { APP_ROUTES } from '@aqx-core/common/constants/router';
import { useUpdateLanguageFromQueryString } from '@aqx-core/common/hooks/language';
import { useSyncLanguage } from '@aqx-core/common/hooks/syncing/language';
import { ELanguageQueryString } from '@aqx-core/common/i18n/languages';
import {
  useAuthenticationAuthenticated,
  useAuthenticationExperienceTokenLoading,
  useAuthenticationLoading
} from '@aqx-core/common/services/auth';
import { useCompanyLoading } from '@aqx-core/common/services/company';

import { useExperienceToken } from '../hooks/use-experience-token';
import AuthenticationRoutes from './auth';
import SecuredRoutes from './secured';

const Router: FCC = () => {
  const loading = useAuthenticationLoading();
  const experienceTokenLoading = useAuthenticationExperienceTokenLoading();

  const authenticated = useAuthenticationAuthenticated();

  const companyLoading = useCompanyLoading();

  const navigate = useNavigate();

  const { search } = useLocation();
  const languageQueryString = new URLSearchParams(search).get('lang') as ELanguageQueryString;
  useUpdateLanguageFromQueryString({ languageQueryString });
  useSyncLanguage();

  const onShowNotification = useCallback((message: string, messageType: EUISnackBarMessageType) => {
    setTimeout(() => {
      showBasicSnackBar({
        message,
        messageType
      });
      // Prevent race condition with switching navigation stacks
    }, 500);
  }, []);

  const onLoginTokenApiSuccess = useCallback(() => {
    navigate(APP_ROUTES.HOME);
  }, [navigate]);

  useExperienceToken({
    source: ELoginSource.WEB,
    onShowNotification,
    onSuccess: onLoginTokenApiSuccess
  });

  const renderState = useMemo<'loading' | 'authentication' | 'secured'>(() => {
    if (loading || companyLoading || experienceTokenLoading) {
      return 'loading';
    }
    if (!authenticated) {
      return 'authentication';
    }
    return 'secured';
  }, [loading, companyLoading, experienceTokenLoading, authenticated]);

  useEffect(() => {
    closeAllPortals();
  }, [renderState]);

  if (renderState === 'loading') {
    return <UILoader absolute />;
  } else if (renderState === 'authentication') {
    return <AuthenticationRoutes />;
  } else if (renderState === 'secured') {
    return <SecuredRoutes />;
  } else {
    return null;
  }
};

export default Router;
